import { useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { useTeamMemberGrantDetails } from "../../queries";
import { useEvaluateMatrixQuery } from "../../queries/allocationMatrix";
import { formatDisplayDate } from "../../utils/date";
import { formatConversionRatio } from "../../utils/string";
import AllocationEnvelopePanel from "../grants/grantInformation/AllocationEnvelopePanel";
import { GrantApprovalCard } from "../esopOverview/GrantApprovalCard";
import { GrantStateBadge } from "./TeamMembersGrid";
import { getCurrencySymbol } from "../../utils/currencyFormatter";
import { useCompanyStore } from "../../store/companyStore";
import { AllocationMode } from "../../store/useAllocationMatrixStore";

const GRANT_STATE_COLORS: Record<string, { dot: string; text: string }> = {
  GRANTED: { dot: "bg-green-500", text: "text-green-700" },
  OFFERED: { dot: "bg-amber-400", text: "text-amber-700" },
  FORFEITED: { dot: "bg-red-400", text: "text-red-600" },
  REJECTED: { dot: "bg-red-400", text: "text-red-600" },
  LAPSED: { dot: "bg-gray-400", text: "text-gray-500" },
};

type Props = {
  grantId: string;
  currentUserId?: string;
  isCurrentUserApprover: boolean;
  onClose?: () => void;
  onViewGrantHistory?: () => void;
};

export function GrantApprovalActionDialog({
  grantId,
  currentUserId,
  isCurrentUserApprover,
  onClose,
  onViewGrantHistory,
}: Props) {
  const { data: grant, isLoading, error } = useTeamMemberGrantDetails(grantId);
  const savedMatchedRule =
    grant?.matchedRule ?? grant?.additionalMetaData?.matchedRule ?? null;
  const { data: evaluatedAllocation } = useEvaluateMatrixQuery(
    grant?.allocationMatrixId,
    grant?.employeeId,
    grant?.id
  );
  const { companyData } = useCompanyStore();
  const allocationMatrixEnabled = Boolean(
    companyData?.companySettings?.grantLetterSettings
      ?.useAllocationMatrixForGranting
  );
  const currencySymbol = getCurrencySymbol();
  const [previewChanges, setPreviewChanges] = useState<Record<string, string>>(
    {}
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <CircularProgress size={30} style={{ color: "#e85936" }} />
      </div>
    );
  }

  if (error || !grant) {
    return (
      <div className="text-red-500 py-6 text-center">
        Failed to load grant details.
      </div>
    );
  }

  const previewOptionsGranted = previewChanges.optionsGranted
    ? Number(previewChanges.optionsGranted)
    : grant.grantedOptions;
  const storedAllocationEvaluation = savedMatchedRule
    ? {
        matched: true,
        rule: savedMatchedRule,
        matchedRule: savedMatchedRule,
        matchedOn: evaluatedAllocation?.matchedOn,
      }
    : evaluatedAllocation ?? null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-gray-100 rounded-lg p-4 bg-gray-50/50">
          <h4 className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wider">
            Grant Information
          </h4>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Identifier:</span>
              <span className="font-semibold text-gray-800">
                {grant.identifier}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">State:</span>
              <GrantStateBadge state={grant.state} />
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Grant Type:</span>
              <span className="font-medium text-gray-800">
                {grant.grantType ?? "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Date of Grant:</span>
              <span className="font-medium text-gray-800">
                {grant.dateOfGrant ? formatDisplayDate(grant.dateOfGrant) : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Vesting Start Date:</span>
              <span className="font-medium text-gray-800">
                {grant.vestingStartDate
                  ? formatDisplayDate(grant.vestingStartDate)
                  : "-"}
              </span>
            </div>
          </div>
        </div>

        <div className="border border-gray-100 rounded-lg p-4 bg-gray-50/50">
          <h4 className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wider">
            Option & Financials
          </h4>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Granted Options:</span>
              <span className="font-semibold text-gray-800">
                {grant.grantedOptions?.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Strike Price:</span>
              <span className="font-semibold text-gray-800">
                {grant.strikePrice
                  ? `${currencySymbol}${grant.strikePrice.toLocaleString()} ${
                      grant.strikePriceType?.replace(/_/g, " ") ?? ""
                    }`
                  : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Grant PPS:</span>
              <span className="font-semibold text-gray-800">
                {grant.pricePerOption != null
                  ? `${currencySymbol}${grant.pricePerOption.toLocaleString()}`
                  : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Plan Name:</span>
              <span className="font-medium text-gray-800">
                {grant.plan?.name ?? "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Conversion Ratio:</span>
              <span className="font-medium text-gray-800">
                {grant.ratio ? formatConversionRatio(grant.ratio) : "1:1"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {allocationMatrixEnabled && grant.allocationMatrixId && (
        <AllocationEnvelopePanel
          overrides={{
            allocationMatrixId: grant.allocationMatrixId,
            employeeId: grant.employeeId,
            employeeName: grant.employeeName,
            grantId: grant.id,
            sharePrice: grant.sharePrice,
            grantPrice: grant.strikePrice,
            optionsGranted: previewOptionsGranted,
            currentSharePrice: grant.currentSharePrice,
            employeeGrants: grant.employeeGrants ?? [],
            embedded: true,
            evaluation: storedAllocationEvaluation,
            onViewGrantHistory,
            managerScoped: true,
          }}
        />
      )}

      <GrantApprovalCard
        grantId={grantId}
        isCurrentUserApprover={isCurrentUserApprover}
        currentUserId={currentUserId}
        defaultExpanded
        recommendationInputMode={
          savedMatchedRule?.allocationMode === AllocationMode.ABSOLUTE_VALUE
            ? "VALUE"
            : "OPTIONS"
        }
        onProposedChangePreview={setPreviewChanges}
        onClose={onClose}
      />
    </div>
  );
}
